/**
 * CompatibilityCard — rendered under a Krishna chat message when the
 * backend returns `suggested_experience.type === "compatibility"`.
 *
 * Shows the 36-point Guna total, strengths, watch-outs and a one-tap
 * "Save as partner profile" button that POSTs to /api/kalki/compat.
 */
import React, { useState } from "react";
import { Users, Save, Check } from "lucide-react";
import { api } from "@/hooks/useAuth";

const INDIGO = "#4F46E5";
const FUCHSIA = "#D946EF";
const INK = "#1C1C1C";
const MUTE = "rgba(28,28,28,0.55)";

export function CompatibilityCard({ experience, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  if (!experience || experience.type !== "compatibility") return null;
  const s = experience.summary || {};
  const g = s.guna || {};
  const total = g.total ?? 0;
  const max = g.max ?? 36;
  const verdict = s.verdict || "Workable";
  const strengths = s.strengths || [];
  const cautions = s.watch_outs || [];

  const handleSave = async () => {
    if (!experience.save_payload || saving || saved) return;
    setSaving(true);
    try {
      await api.post("/kalki/compat", experience.save_payload);
      setSaved(true);
      onSaved?.();
    } catch (e) {
      console.warn("[B2] save partner failed", e?.message || e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mt-3 rounded-2xl px-4 py-3"
      style={{
        background: "linear-gradient(135deg, rgba(79,70,229,0.06), rgba(217,70,239,0.04))",
        border: "1px solid rgba(79,70,229,0.14)",
        backdropFilter: "blur(10px)",
      }}
      data-testid="compat-card"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Users className="w-3.5 h-3.5" style={{ color: INDIGO }} />
          <span className="text-[10px] tracking-[0.32em]" style={{ color: INDIGO }}>
            ✦ GUNA MILAN
          </span>
        </div>
        <span className="text-[13px]" style={{ color: INK, fontWeight: 600 }} data-testid="compat-score">
          {total}/{max}
        </span>
      </div>
      <div className="text-[12px] mb-2" style={{ color: INK }} data-testid="compat-verdict">
        {verdict}
      </div>
      {strengths.length > 0 && (
        <ul className="text-[12px] mb-1" style={{ color: INK }}>
          {strengths.slice(0, 2).map((t, i) => (
            <li key={i} className="flex gap-1.5"><span style={{ color: INDIGO }}>•</span>{t}</li>
          ))}
        </ul>
      )}
      {cautions.length > 0 && (
        <ul className="text-[12px] mb-2" style={{ color: MUTE }}>
          {cautions.slice(0, 2).map((t, i) => (
            <li key={i} className="flex gap-1.5"><span style={{ color: FUCHSIA }}>•</span>{t}</li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={handleSave}
        disabled={saving || saved || !experience.save_payload}
        className="inline-flex items-center gap-1.5 rounded-full border transition-all"
        style={{
          padding: "6px 12px",
          borderColor: `${INDIGO}40`,
          background: `${INDIGO}08`,
          color: INK,
          fontSize: 11,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          fontWeight: 500,
          opacity: saved ? 0.6 : 1,
          cursor: saved ? "default" : "pointer",
        }}
        data-testid="compat-save-partner"
      >
        {saved ? <Check className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
        {saved ? "Saved" : saving ? "Saving…" : "Save as partner profile"}
      </button>
    </div>
  );
}

export default CompatibilityCard;
